/**
 * Multi-select dropdown: trigger shows selection count or labels; popover with optional search and checkboxes.
 * Used for "mix option" families in admin and catalogue converter.
 */

import * as React from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  /** Exclude these option values from the list (e.g. current family when editing). */
  excludeValues?: string[];
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  /** When set, show a search input inside the popover to filter options by label. */
  searchPlaceholder?: string;
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  excludeValues = [],
  disabled = false,
  className,
  triggerClassName,
  searchPlaceholder,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  React.useEffect(() => { if (!open) setSearchQuery(''); }, [open]);
  const baseOptions = options.filter((o) => !excludeValues.includes(o.value));
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) return baseOptions;
    const q = searchQuery.trim().toLowerCase();
    return baseOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [baseOptions, searchQuery]);
  const selectedOptions = baseOptions.filter((o) => value.includes(o.value));

  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const clearOne = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== optionValue));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('min-h-10 justify-between font-normal', triggerClassName)}
        >
          <span className="flex flex-wrap items-center gap-1.5 truncate">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : selectedOptions.length <= 2 ? (
              selectedOptions.map((o) => (
                <span
                  key={o.value}
                  className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-xs"
                >
                  {o.label}
                  <button
                    type="button"
                    onClick={(e) => clearOne(e, o.value)}
                    className="rounded hover:bg-primary/20"
                    aria-label={`Quitar ${o.label}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-sm">{selectedOptions.length} seleccionadas</span>
            )}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[var(--radix-popover-trigger-width)] min-w-[280px] max-w-[min(400px,95vw)] p-0', className)} align="start">
        {searchPlaceholder != null && (
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="pl-8 h-9"
              />
            </div>
          </div>
        )}
        <ScrollArea className="max-h-60">
          <div className="p-1">
            {filteredOptions.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {searchQuery.trim() ? 'No hay coincidencias' : 'No hay opciones'}
              </p>
            ) : (
              filteredOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
                >
                  <Checkbox
                    checked={value.includes(option.value)}
                    onCheckedChange={() => toggle(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
